import MenuIcon from '@mui/icons-material/Menu';
import {
  Badge,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { managerColors } from '../ui/theme';
import ManagerHeader from './ManagerHeader';
import { MANAGER_NAV, type BadgeKey } from './managerNav';
import { useManagerBadges } from './useManagerBadges';

const SIDEBAR_WIDTH = 248;

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  const badges = useManagerBadges();
  const isActive = (to: string, exact?: boolean) =>
    exact
      ? location.pathname === to
      : location.pathname === to || location.pathname.startsWith(`${to}/`);
  return (
    <Box
      sx={{
        height: '100%',
        bgcolor: managerColors.sidebar,
        color: '#E2E8F0',
        overflowY: 'auto',
        pb: 3,
      }}
    >
      <Box sx={{ px: 2.5, py: 2.25, borderBottom: '1px solid #334155' }}>
        <Typography
          sx={{
            fontFamily: "'Manrope', sans-serif",
            fontWeight: 800,
            color: '#FFFFFF',
            fontSize: '1.1rem',
          }}
        >
          Registry
        </Typography>
        <Typography variant="caption" sx={{ color: '#94A3B8' }}>
          Admin module · prototype
        </Typography>
      </Box>
      {MANAGER_NAV.map((group) => (
        <Box key={group.label} sx={{ mt: 2 }}>
          <Typography
            variant="caption"
            sx={{
              px: 2.5,
              color: '#94A3B8',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {group.label}
          </Typography>
          <List dense disablePadding sx={{ mt: 0.5 }}>
            {group.items.map((item) => {
              const active = isActive(item.to, item.exact);
              const count = item.badge ? badges[item.badge as BadgeKey] : 0;
              const Icon = item.icon;
              return (
                <ListItemButton
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  onClick={onNavigate}
                  selected={active}
                  sx={{
                    mx: 1,
                    borderRadius: 1.5,
                    color: active ? '#FFFFFF' : '#CBD5E1',
                    '&.Mui-selected': { bgcolor: managerColors.sidebarActive },
                    '&.Mui-selected:hover, &:hover': { bgcolor: '#2B3A50' },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: 'inherit' }}>
                    <Icon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{
                      fontSize: '0.875rem',
                      fontWeight: active ? 600 : 500,
                    }}
                  />
                  {count > 0 && (
                    <Badge
                      badgeContent={count}
                      color="warning"
                      sx={{ mr: 1.5, '& .MuiBadge-badge': { fontWeight: 700 } }}
                    />
                  )}
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
}

export default function ManagerShell({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box
        component="nav"
        sx={{ width: { md: SIDEBAR_WIDTH }, flexShrink: 0 }}
        aria-label="Registry navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          sx={{ display: { md: 'none' }, '& .MuiDrawer-paper': { width: SIDEBAR_WIDTH } }}
        >
          <SidebarContent onNavigate={() => setMobileOpen(false)} />
        </Drawer>
        <Box
          sx={{
            display: { xs: 'none', md: 'block' },
            position: 'fixed',
            width: SIDEBAR_WIDTH,
            top: 0,
            bottom: 0,
          }}
        >
          <SidebarContent />
        </Box>
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ManagerHeader
          menuButton={
            <IconButton
              onClick={() => setMobileOpen(true)}
              sx={{ display: { md: 'none' } }}
              aria-label="Open navigation"
            >
              <MenuIcon />
            </IconButton>
          }
        />
        <Box component="main" sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1320, mx: 'auto' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
}
